/**
 * Curated logo-circle rails (leagues + streaming platforms), sourced from the
 * onboarding-tags list. Logos live in the public bucket under
 * `discovery-logos/<slug>.<ext>` (uploaded by `scripts/fetch-logos.sh`) and are
 * served from the public CDN host, so URLs resolve per environment.
 */

import { publicImageHost } from "src/utils/imageUrl.js";

/**
 * Wikimedia Commons logo filenames per slug. Single source of truth shared by
 * the hotlink helper below and the fetch-logos upload script.
 */
export const LOGO_FILES = {
  netflix: "Netflix_2015_logo.svg",
  max: "HBO_Max_Logo.svg",
  "prime-video": "Amazon_Prime_Video_logo.svg",
  "disney-plus": "Disney+_2024.svg",
  hulu: "Hulu_logo_(2018).svg",
  "apple-tv": "Apple_TV_Plus_Logo.svg",
  peacock: "NBCUniversal_Peacock_Logo.svg",
  "paramount-plus": "Paramount+_logo.svg",
  espn: "ESPN_wordmark.svg",
  nba: "NBA_logo.svg",
  mlb: "Major_League_Baseball_logo.svg",
  nhl: "National_Hockey_League_shield.svg",
  mls: "MLS_crest_logo_RGB_gradient.svg",
  fifa: "FIFA_logo_without_slogan.svg",
  "premier-league": "Premier_League.svg",
  "champions-league": "UEFA_Champions_League_logo.svg",
  laliga: "LaLiga_logo_2023.svg",
  bundesliga: "Bundesliga_logo.svg",
  "serie-a": "Serie_A_logo_2022.svg",
  nfl: "National_Football_League_logo.webp",
  cbs: "CBS_logo.svg",
  nbc: "NBC_logo.svg",
  abc: "American_Broadcasting_Company_Logo.svg",
  fox: "Fox_Broadcasting_Company_logo_(2019).svg",
  "the-cw": "The_CW.svg",
  pbs: "PBS_logo_2019.svg",
  tnt: "TNT_Logo_2016.svg",
  tbs: "TBS_logo_2016.svg",
  "usa-network": "USA_Network_logo_(2016).svg",
  fx: "FX_International_logo.svg",
  amc: "AMC_logo_2019.svg",
  cnn: "CNN.svg",
  msnbc: "MSNBC_2015_logo.svg",
  hgtv: "HGTV_US_Logo_2015.svg",
  "food-network": "Food_Network_logo.svg",
  univision: "Logo_Univision_2013.svg",
  telemundo: "Telemundo_logo_2018.svg",
  youtube: "YouTube_Logo_2017.svg",
  "bbc-one": "BBC_One_logo_2021.svg",
  "bbc-two": "BBC_Two_logo_2021.svg",
  "bbc-three": "BBC_Three_2022.svg",
  "bbc-four": "BBC_Four_logo_2021.svg",
  "bbc-iplayer": "BBC_iPlayer_(2021).svg",
  "bbc-news": "BBC_News_2019.svg",
  "channel-4": "Channel_4_2022.svg",
  itv: "ITV1_Logo_2022.svg",
  "sky-news": "Sky_News_2020.svg",
  "sky-arts": "Logo_de_Sky_Arts.png",
  s4c: "S4C_logo_2014.svg",
  rte: "RTÉ_logo.svg",
  "national-geographic": "Natgeologo.svg",
  "comedy-central": "Comedy_Central_2018.svg",
  mtv: "MTV_2021_(brand_version).svg",
  "vice-tv": "Viceland.svg",
  oxygen: "Oxygen_TV_(2017_Logo).svg",
  bravo: "Bravo_2017_logo.svg",
  "a-e": "A&E_Network_logo.svg",
  history: "History_(2021).svg",
  lifetime: "Lifetime_tv_logo.svg",
  discovery: "Discovery_Channel_-_Logo_2019.svg",
  "discovery-plus": "Discovery_Plus_logo.svg",
  "investigation-discovery": "Investigation_Discovery_Logo_2018.svg",
  tlc: "TLC_Logo.svg",
  nickelodeon: "Nickelodeon_2009_logo.svg",
  "cartoon-network": "Cartoon_Network_2010_logo.svg",
  "adult-swim": "Adult_Swim_2003_logo.svg",
  syfy: "SYFY.svg",
  showtime: "Showtime.svg",
  starz: "Starz_2022.svg",
  "mgm-plus": "MGM+_logo.svg",
  freeform: "Freeform_2018.svg",
  trutv: "TruTV_2014.svg",
  e: "E!_Logo_2012.svg",
  "fox-news": "Fox_News_Channel_logo.svg",
  "fox-nation": "Fox_Nation_logo.svg",
  cnbc: "CNBC_logo.svg",
  newsnation: "NewsNation_logo.svg",
  "wwe-network": "WWE_Network_logo.svg",
  roku: "Roku_logo.svg",
  "paramount-network": "Paramount_Network.svg",
  hallmark: "Hallmark_Channel.svg",
  "disney-channel": "2014_Disney_Channel_logo.svg",
  bet: "BET-2021Logo.svg",
  dropout: "Dropout_logo_bug_black.svg",
  twit: "TWiT.svg",
  sbs: "SBS_Logo_2019.svg",
  nine: "Nine_Network_logo_(2008).svg",
  seven: "Seven_Network_logo.svg",
  "network-10": "Network_Ten_logo_(solid_yellow).svg",
  stan: "Stan_logo.svg",
  tf1: "Logo_TF1_2013.svg",
  "france-2": "France_2_2018.svg",
  "canal-plus": "Canal+.svg",
  zdf: "ZDF_logo.svg",
  "das-erste": "Das_Erste_2014.svg",
  nhk: "NHK_logo.svg",
  crave: "CraveTV_logo.svg",
  cbc: "CBC_Logo_1992-Present.svg",
  ctv: "CTV_flat_logo.svg",
  kbs2: "KBS_2_logo.svg",
  sec: "Southeastern Conference logo (2024).svg",
  "big-10": "Big Ten Conference logo.svg",
  acc: "Atlantic Coast Conference logo.svg",
  "big-12": "Big 12 Conference logo.svg",
  "big-east": "Big East Conference logo.svg",
  aac: "American Athletic Conference logo.svg",
  "america-east": "America East Conference logo 2024.svg",
  "atlantic-10": "Atlantic 10 Conference logo.svg",
  "atlantic-sun": "Atlantic Sun Conference logo.svg",
  "big-sky": "Big Sky Conference logo.svg",
  "big-south": "Big South Conference logo.svg",
  "big-west": "Big West Conference logo 2021.svg",
  "coastal-athletic": "Coastal Athletic Association logo.svg",
  horizon: "Horizon League 2024 logo.svg",
  ivy: "Ivy league simple logo.png",
  maac: "Metro Atlantic Athletic Conference logo.svg",
  mac: "Mid-American Conference logo.svg",
  meac: "Mid-Eastern Athletic Conference logo.svg",
  "missouri-valley": "Missouri Valley Conference logo.svg",
  "mountain-west": "Mountain West Conference logo.svg",
  northeast: "Northeast Conference logo.svg",
  "ohio-valley": "Ohio Valley Conference logo.svg",
  "patriot-league": "Patriot league conference logo.svg",
  southern: "Southern Conference logo.svg",
  southland: "Southland Conference primary logo.svg",
  "summit-league": "Summit League logo.svg",
  sunbelt: "Sun Belt Conference 2020 logo and name.svg",
  swac: "Southwestern Athletic Conference logo.svg",
  wac: "Western Athletic Conference logo.svg",
  "west-coast": "West Coast Conference logo 2019 plain.svg",
  "ligue-1": "Ligue 1 McDonald's logo.svg",
  eredivisie: "Eredivisie nieuw logo 2017-.svg",
  "efl-championship": "EFL Championship Wordmark.svg",
  "efl-league-one": "EFL League One Wordmark.svg",
  "uefa-europa-league": "UEFA Europa League logo (2024 version).svg",
  "uefa-conference-league": "UEFA Europa Conference League logo.svg",
  "uefa-nations-league": "Uefa nations league textlogo.png",
  "coppa-italia": "Logo of Coppa Italia Frecciarossa (2024-2025).svg",
  "concacaf-gold-cup": "Concacaf Gold Cup 2021.svg",
  usl: "USL Championship horz dark logo.svg",
  ahl: "American Hockey League Logo.svg",
  "nrl-premiership": "NRL logo.svg",
  milb: "New_MiLBLogo-Smaller.png",
  "australian-open": "Australian Open Logo 2017.svg",
  "french-open": "Logo_Roland-Garros.svg.webp",
  wimbledon: "Wimbledon.svg",
  "us-open": "Usopen-horizontal-logo.svg",
  "formula-1": "Formula 1 logo.svg",
  nascar: "NASCAR logo 2017.svg",
  wnba: "WNBA logo.png",
  pwhl: "Professional Women's Hockey League.svg",
  tubi: "Tubi.svg",
  crunchyroll: "Crunchyroll.svg",
  britbox: "BritBox 2026.svg",
  "acorn-tv": "Acorn TV logo 2024.svg",
};

/** Slugs with a hosted brand-mark icon (`<slug>-icon.svg`) from Simple Icons. */
const SIMPLE_ICON_SLUGS = new Set([
  "netflix",
  "max",
  "apple-tv",
  "paramount-plus",
  "cbs",
  "nbc",
  "fox",
  "cnn",
]);

/** tvmaze network names (lowercased) → logo slug, for the tile network badge. */
const NETWORK_NAME_TO_SLUG = {
  netflix: "netflix",
  "hbo max": "max",
  max: "max",
  hbo: "max",
  "prime video": "prime-video",
  "amazon prime video": "prime-video",
  "amazon prime": "prime-video",
  "disney+": "disney-plus",
  "disney plus": "disney-plus",
  hulu: "hulu",
  "apple tv+": "apple-tv",
  "apple tv plus": "apple-tv",
  "apple tv": "apple-tv",
  peacock: "peacock",
  "paramount+": "paramount-plus",
  "paramount plus": "paramount-plus",
  espn: "espn",
  "espn+": "espn",
  cbs: "cbs",
  nbc: "nbc",
  abc: "abc",
  fox: "fox",
  "the cw": "the-cw",
  cw: "the-cw",
  pbs: "pbs",
  tnt: "tnt",
  tbs: "tbs",
  "usa network": "usa-network",
  usa: "usa-network",
  fx: "fx",
  amc: "amc",
  cnn: "cnn",
  msnbc: "msnbc",
  hgtv: "hgtv",
  "food network": "food-network",
  bet: "bet",
  "bet+": "bet",
  univision: "univision",
  telemundo: "telemundo",
  youtube: "youtube",
  "bbc one": "bbc-one",
  "bbc one northern ireland": "bbc-one",
  "bbc one wales": "bbc-one",
  "bbc one scotland": "bbc-one",
  "bbc two": "bbc-two",
  "bbc three": "bbc-three",
  "bbc four": "bbc-four",
  "bbc iplayer": "bbc-iplayer",
  "bbc news": "bbc-news",
  "bbc news (uk)": "bbc-news",
  "bbc news (europe)": "bbc-news",
  "bbc world news": "bbc-news",
  "channel 4": "channel-4",
  itv1: "itv",
  itv2: "itv",
  itv3: "itv",
  itv4: "itv",
  itvx: "itv",
  itvbe: "itv",
  "itv wales": "itv",
  "sky news": "sky-news",
  "sky arts": "sky-arts",
  s4c: "s4c",
  "rté one": "rte",
  "rté2": "rte",
  "rté news": "rte",
  "rté player": "rte",
  "national geographic": "national-geographic",
  "national geographic wild": "national-geographic",
  "nat geo tv": "national-geographic",
  "comedy central": "comedy-central",
  mtv: "mtv",
  "mtv live": "mtv",
  palladia: "mtv",
  "vice tv": "vice-tv",
  viceland: "vice-tv",
  oxygen: "oxygen",
  "oxygen true crime": "oxygen",
  bravo: "bravo",
  "a&e": "a-e",
  history: "history",
  lifetime: "lifetime",
  discovery: "discovery",
  "discovery+": "discovery-plus",
  "investigation discovery": "investigation-discovery",
  tlc: "tlc",
  nickelodeon: "nickelodeon",
  "cartoon network": "cartoon-network",
  "adult swim": "adult-swim",
  syfy: "syfy",
  showtime: "showtime",
  "showtime on demand": "showtime",
  "paramount+ with showtime": "paramount-plus",
  starz: "starz",
  "mgm+": "mgm-plus",
  freeform: "freeform",
  trutv: "trutv",
  "e!": "e",
  "fox news channel": "fox-news",
  "fox nation": "fox-nation",
  cnbc: "cnbc",
  newsnation: "newsnation",
  "wwe network": "wwe-network",
  "the roku channel": "roku",
  "paramount network": "paramount-network",
  "hallmark channel": "hallmark",
  "hallmark+": "hallmark",
  "disney channel": "disney-channel",
  dropout: "dropout",
  twit: "twit",
  sbs: "sbs",
  "sbs on demand": "sbs",
  "nine network": "nine",
  "seven network": "seven",
  "network 10": "network-10",
  stan: "stan",
  tf1: "tf1",
  "tf1+": "tf1",
  "france 2": "france-2",
  "canal+": "canal-plus",
  zdf: "zdf",
  "das erste": "das-erste",
  nhk: "nhk",
  "nhk world": "nhk",
  crave: "crave",
  cbc: "cbc",
  "cbc gem": "cbc",
  ctv: "ctv",
  kbs2: "kbs2",
  "cbs news": "cbs",
  "abc news": "abc",
  "abc news live": "abc",
  "nbc.com": "nbc",
  espn2: "espn",
  espnu: "espn",
  "cnn international": "cnn",
  "cnn max": "cnn",
  "nfl network": "nfl",
  "nfl+": "nfl",
  "mlb network": "mlb",
  fxx: "fx",
  "amc+": "amc",
  "disney+ hotstar": "disney-plus",
  "pbs kids": "pbs",
  "pbs america": "pbs",
};

/** Pulls the network name out of a "📺 Watch on <network> | ..." description. */
const WATCH_ON_RE = /Watch on\s+([^|\n]+)/i;

/**
 * Resolves a slug to its hosted logo URL, or "" when we have no logo yet (the
 * circle then shows fallback initials).
 *
 * @param {string} slug
 * @returns {string}
 */
const logoUrl = (slug) => {
  const file = LOGO_FILES[slug];
  if (!file) return "";
  const ext = file.split(".").pop().toLowerCase();
  return `${publicImageHost()}/discovery-logos/${slug}.${ext}`;
};

/**
 * The logo for a network slug: the brand-mark icon where we have one, otherwise
 * the wordmark (or "" when we host neither).
 *
 * @param {string} slug
 * @returns {{ logoUrl: string, isIcon: boolean }}
 */
const networkLogo = (slug) => {
  if (SIMPLE_ICON_SLUGS.has(slug)) {
    return { logoUrl: `${publicImageHost()}/discovery-logos/${slug}-icon.svg`, isIcon: true };
  }
  return { logoUrl: logoUrl(slug), isIcon: false };
};

/**
 * Resolves a TV calendar's "Watch on <network>" marker to a network badge, or
 * null when the description carries no marker. Networks without a hosted logo
 * fall back to an empty logoUrl, which the badge renders as initials.
 *
 * @param {string|null|undefined} description
 * @returns {{ slug: string|null, name: string, logoUrl: string, isIcon: boolean }|null}
 */
export function networkBadgeFromDescription(description) {
  const match = WATCH_ON_RE.exec(description || "");
  if (!match) return null;
  const name = match[1].trim();
  if (!name) return null;
  const slug = NETWORK_NAME_TO_SLUG[name.toLowerCase()] ?? null;
  if (!slug) return { slug, name, logoUrl: "", isIcon: false };
  return { slug, name, ...networkLogo(slug) };
}

/** @param {{ name: string, slug: string, tag: string }} entry */
const toCircle = ({ name, slug, tag }) => ({
  key: slug,
  name,
  logoUrl: logoUrl(slug),
  to: `/tag/${tag}`,
});

export const MAJOR_LEAGUES = [
  { name: "NFL", slug: "nfl", tag: "nfl" },
  { name: "NBA", slug: "nba", tag: "nba" },
  { name: "MLB", slug: "mlb", tag: "mlb" },
  { name: "NHL", slug: "nhl", tag: "nhl" },
  { name: "MLS", slug: "mls", tag: "mls" },
  { name: "Premier League", slug: "premier-league", tag: "premier-league" },
  { name: "Champions League", slug: "champions-league", tag: "champions-league" },
  { name: "La Liga", slug: "laliga", tag: "laliga" },
  { name: "Bundesliga", slug: "bundesliga", tag: "bundesliga" },
  { name: "Serie A", slug: "serie-a", tag: "serie-a" },
  { name: "FIFA", slug: "fifa", tag: "fifa" },
];

export const LEAGUE_CIRCLES = MAJOR_LEAGUES.map(toCircle);

/** College conference circles, in editorial priority order. */
export const COLLEGE_CONFERENCE_CIRCLES = [
  { name: "SEC", slug: "sec", tag: "sec" },
  { name: "Big Ten", slug: "big-10", tag: "big-10" },
  { name: "Big 12", slug: "big-12", tag: "big-12" },
  { name: "ACC", slug: "acc", tag: "acc" },
  { name: "AAC", slug: "aac", tag: "aac" },
  { name: "Mountain West", slug: "mountain-west", tag: "mountain-west" },
  { name: "MAC", slug: "mac", tag: "mac" },
  { name: "Big East", slug: "big-east", tag: "big-east" },
  { name: "Ivy League", slug: "ivy", tag: "ivy" },
  { name: "MEAC", slug: "meac", tag: "meac" },
].map(toCircle);

/** Circle-rail drill-downs keyed by tag slug, rendered below a tag's leaderboard. */
export const CIRCLE_RAILS_BY_TAG = {
  ncaa: { title: "College Conferences", items: COLLEGE_CONFERENCE_CIRCLES },
};

export const STREAMING_CIRCLES = [
  { name: "Netflix", slug: "netflix", tag: "netflix" },
  { name: "Max", slug: "max", tag: "hbo" },
  { name: "Prime Video", slug: "prime-video", tag: "prime-video" },
  { name: "Disney+", slug: "disney-plus", tag: "disney" },
  { name: "Hulu", slug: "hulu", tag: "hulu" },
  { name: "Apple TV+", slug: "apple-tv", tag: "apple-tv" },
  { name: "Peacock", slug: "peacock", tag: "peacock" },
  { name: "Paramount+", slug: "paramount-plus", tag: "paramount" },
  { name: "ESPN+", slug: "espn", tag: "espn" },
  { name: "Starz", slug: "starz", tag: "starz" },
  { name: "Discovery+", slug: "discovery-plus", tag: "discovery" },
  { name: "YouTube", slug: "youtube", tag: "youtube" },
  { name: "Tubi", slug: "tubi", tag: "tubi" },
  { name: "Crunchyroll", slug: "crunchyroll", tag: "crunchyroll" },
  { name: "BritBox", slug: "britbox", tag: "britbox" },
  { name: "Acorn TV", slug: "acorn-tv", tag: "acorn-tv" },
].map(toCircle);

export const TV_NETWORK_CIRCLES = [
  { name: "CBS", slug: "cbs", tag: "cbs" },
  { name: "NBC", slug: "nbc", tag: "nbc" },
  { name: "ABC", slug: "abc", tag: "abc" },
  { name: "FOX", slug: "fox", tag: "fox" },
  { name: "The CW", slug: "the-cw", tag: "the-cw" },
  { name: "PBS", slug: "pbs", tag: "pbs" },
  { name: "TNT", slug: "tnt", tag: "tnt" },
  { name: "TBS", slug: "tbs", tag: "tbs" },
  { name: "USA Network", slug: "usa-network", tag: "usa-network" },
  { name: "FX", slug: "fx", tag: "fx" },
  { name: "AMC", slug: "amc", tag: "amc" },
  { name: "CNN", slug: "cnn", tag: "cnn" },
  { name: "MSNBC", slug: "msnbc", tag: "msnbc" },
  { name: "HGTV", slug: "hgtv", tag: "hgtv" },
  { name: "Food Network", slug: "food-network", tag: "food-network" },
  { name: "Bravo", slug: "bravo", tag: "bravo" },
  { name: "TLC", slug: "tlc", tag: "tlc" },
  { name: "Lifetime", slug: "lifetime", tag: "lifetime" },
  { name: "History", slug: "history", tag: "history-channel" },
  { name: "BET", slug: "bet", tag: "bet" },
  { name: "Univision", slug: "univision", tag: "univision" },
  { name: "Telemundo", slug: "telemundo", tag: "telemundo" },
].map(toCircle);
